import { useEffect, useRef } from "react";

import { Box, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import {
  SOFT_LINE,
  cardAttentionChips,
  dateBasisLabel,
  eventNoun,
  isSoftDate,
  isTrade,
  pipelineStatusLabel,
  readinessMeta,
} from "../../components/closingReadiness/closingReadinessUtils";
import { amber, slate, status, teal } from "../../theme/colors";
import {
  C,
  MONO,
  loanAmountCompact,
  loanChipLabel,
  statusMetaForLoan,
} from "./closingCalendarUtils";

// The chip whose loan is open in the detail drawer: level-1 (teal) selection —
// tint, teal edge bar and border, a soft ring — laid over the readiness fill.
const SELECTED_SX = {
  background: teal[100],
  border: `1px solid ${teal[500]}`,
  opacity: 1,
  boxShadow: `0 0 0 2px ${alpha(teal[500], 0.18)}`,
  "&::before": { background: teal[500] },
  "&:hover": { background: teal[100] },
};

// Scroll the chip into view when it becomes the selected one (the drawer's
// ‹ › pager can land on a loan in another row / week).
const useScrollWhenSelected = (selected) => {
  const ref = useRef(null);
  useEffect(() => {
    if (selected && ref.current?.scrollIntoView) {
      ref.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selected]);
  return ref;
};

// The at-a-glance marks, rendered the SAME way on both chip sizes so a loan
// reads identically in the month cell and in the day's "+N more" popover:
//   TR        an acquisition (trade settlement)
//   z         stale — no activity for 7+ days
//   amber dot expected-date churn
//   red dot   any hard red flag (date passed, missed, wire-blocked, cutoff, lock)
// One dot per colour, never a dot per flag — the tooltip lists the details.
const FlagMarks = ({ trade, flags, closed }) => {
  const stale = !closed && flags.some((f) => f.key === "stale");
  const churn = !closed && flags.some((f) => f.key === "churn");
  const red = !closed && flags.some((f) => f.key !== "stale" && f.key !== "churn");
  if (!trade && !stale && !churn && !red) return null;
  return (
    <Flx ac g={0.5} sx={{ flexShrink: 0 }}>
      {trade && (
        <Txt sx={{ fontSize: 8.5, fontWeight: 700, letterSpacing: "0.04em", color: C.faint, flexShrink: 0 }}>
          TR
        </Txt>
      )}
      {stale && (
        <Txt sx={{ fontSize: 9.5, fontWeight: 700, color: amber.dark, flexShrink: 0 }}>z</Txt>
      )}
      {churn && (
        <Box sx={{ width: 6, height: 6, borderRadius: "99px", background: amber.chipText, flexShrink: 0 }} />
      )}
      {red && (
        <Box sx={{ width: 6, height: 6, borderRadius: "99px", background: status.error.solid, flexShrink: 0 }} />
      )}
    </Flx>
  );
};

// The single-line chip for dense month cells: edge bar · #loan · EV · TR · z · amount.
// The fill still encodes date confidence (tinted = firm, white = expected) and
// the edge the readiness level; the trade / stale marks are the two flags the
// coverage calendar's legend spells out, and a red dot stands in for any hard
// red flag (date passed, missed, wire-blocked, cutoff, lock).
const CompactChip = ({
  card,
  s,
  closed,
  soft,
  trade,
  flags,
  amount,
  onClick,
  selected,
}) => {
  return (
    <Flx
      ac
      g={0.6}
      onClick={(e) => {
        e.stopPropagation();
        onClick(card);
      }}
      sx={{
        position: "relative",
        mb: "3px",
        // Room between the edge bar and the label, like the two-line chip.
        pl: 1.75,
        pr: 0.85,
        py: "4px",
        borderRadius: "6px",
        // Fill = date confidence, in a NEUTRAL voice — the level colour lives
        // on the edge bar only. A firm date fills in the level tint; an
        // expected-only date stays white with a grey dashed border.
        background: closed || soft ? "#fff" : alpha(s.c, 0.11),
        border: closed
          ? `1px solid ${slate[200]}`
          : soft
            ? `1px dashed ${SOFT_LINE}`
            : `1px solid ${alpha(s.c, 0.2)}`,
        opacity: closed ? 0.55 : 1,
        cursor: "pointer",
        minWidth: 0,
        // Every mark gets the same line box regardless of its font / size, so
        // the loan #, the mono EV id, the TR / z marks and the amount all sit
        // on one visual centre. (Each on its own `lineHeight: 1` they float to
        // different heights because the fonts' metrics differ.)
        "& > *, & > * > *": { lineHeight: "14px" },
        transition: "background 0.1s, filter 0.1s",
        "&::before": {
          content: '""',
          position: "absolute",
          left: 4,
          top: 4,
          bottom: 4,
          width: 3,
          borderRadius: "3px",
          background: s.c,
        },
        "&:hover": {
          background: closed || soft ? slate[50] : alpha(s.c, 0.16),
        },
        ...(selected ? SELECTED_SX : {}),
      }}
    >
      <Txt
        sx={{
          fontSize: 11.5,
          fontWeight: closed ? 400 : 600,
          color: closed ? slate[400] : C.ink,
          textDecoration: closed ? "line-through" : "none",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
          flexShrink: 0,
        }}
      >
        {loanChipLabel(card).replace(/^Loan /, "")}
      </Txt>
      {card?.syncLoanId && (
        <Txt
          sx={{
            fontFamily: MONO,
            fontSize: 10,
            color: C.faint,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
            flexShrink: 1,
          }}
        >
          {card.syncLoanId}
        </Txt>
      )}
      <FlagMarks trade={trade} flags={flags} closed={closed} />
      {amount && (
        <Txt
          sx={{
            ml: "auto",
            fontFamily: MONO,
            fontSize: 10.5,
            color: soft || closed ? C.faint : C.sub,
            flexShrink: 0,
          }}
        >
          {amount}
        </Txt>
      )}
    </Flx>
  );
};

// One schedule card as a calendar chip: readiness bar · loan # + amount, with
// the sync id / date basis on a second line and the at-a-glance marks (FlagMarks).
// `compact` collapses it to one line for dense month cells (CompactChip).
//
// Rendering rules from closing-readiness.md §2:
//   closed          → muted "closed" style, never a pending funding
//   dateBasis=expected → hollow / dashed (no firm date yet)
//   stale / ECD churn / missed / date passed → must be spottable at a glance
//   trade (acquisition) → labelled as a settlement, not a closing
export const ClosingChip = ({
  loan: card,
  onClick,
  dense,
  compact,
  selected = false,
}) => {
  const chipRef = useScrollWhenSelected(selected);
  const s = statusMetaForLoan(card);
  const amount = loanAmountCompact(card);
  const syncId = card?.syncLoanId;
  const soft = isSoftDate(card);
  const closed = !!card?.closed;
  const flags = cardAttentionChips(card).filter(
    (f) => f.key !== "closer" && f.key !== "closed",
  );
  const trade = isTrade(card);
  const tip = [
    `${loanChipLabel(card)} — ${card?.loanName || ""}`.trim(),
    `${pipelineStatusLabel(card)} · ${readinessMeta(card?.readinessLevel).label}`,
    `${eventNoun(card)} · ${dateBasisLabel(card)}`,
    ...(Array.isArray(card?.reasons) ? card.reasons.slice(0, 3) : []),
    ...flags.map((f) => f.label),
  ]
    .filter(Boolean)
    .join("\n");

  if (compact) {
    return (
      <Tooltip
        title={<Box sx={{ whiteSpace: "pre-line", fontSize: 11.5 }}>{tip}</Box>}
        arrow
        enterDelay={400}
      >
        <Box ref={chipRef} sx={{ minWidth: 0 }}>
          <CompactChip
            card={card}
            s={s}
            closed={closed}
            soft={soft}
            trade={trade}
            flags={flags}
            amount={amount}
            onClick={onClick}
            selected={selected}
          />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={<Box sx={{ whiteSpace: "pre-line", fontSize: 11.5 }}>{tip}</Box>}
      arrow
      enterDelay={400}
    >
      <Box ref={chipRef} sx={{ minWidth: 0 }}>
        <Flx
          g={dense ? 0.75 : 0.9}
          onClick={(e) => {
            e.stopPropagation();
            onClick(card);
          }}
          sx={{
            alignItems: "stretch",
            mb: 0.5,
            px: dense ? 0.85 : 1,
            py: dense ? 0.5 : 0.6,
            borderRadius: "7px",
            background: closed ? slate[50] : soft ? "transparent" : s.bg,
            color: s.c,
            border: `1px ${soft ? "dashed" : "solid"} ${soft ? SOFT_LINE : closed ? slate[200] : "transparent"}`,
            opacity: closed ? 0.75 : 1,
            cursor: "pointer",
            lineHeight: 1.25,
            minWidth: 0,
            transition: "border-color 0.1s, box-shadow 0.1s, transform 0.1s",
            "&:hover": {
              borderColor: "currentColor",
              transform: "translateY(-1px)",
              boxShadow: "0 1px 2px rgba(20,30,50,.1)",
            },
            ...(selected
              ? {
                  background: teal[100],
                  borderColor: teal[500],
                  borderStyle: "solid",
                  opacity: 1,
                  boxShadow: `0 0 0 2px ${alpha(teal[500], 0.18)}`,
                  "&:hover": { borderColor: teal[500] },
                }
              : {}),
          }}
        >
          <Box
            sx={{
              width: 3,
              alignSelf: "stretch",
              borderRadius: "2px",
              background: selected ? teal[500] : s.c,
              flexShrink: 0,
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Flx ac jb g={0.75}>
              <Txt
                sx={{
                  fontSize: dense ? 11.5 : 12,
                  fontWeight: 600,
                  color: closed ? slate[500] : C.ink,
                  textDecoration: closed ? "line-through" : "none",
                  flex: 1,
                  minWidth: 0,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                }}
              >
                {loanChipLabel(card)}
              </Txt>
              {amount && (
                <Txt
                  sx={{
                    fontFamily: MONO,
                    fontSize: dense ? 10 : 10.5,
                    fontWeight: 500,
                    color: C.sub,
                    flexShrink: 0,
                  }}
                >
                  {amount}
                </Txt>
              )}
            </Flx>
            <Flx ac g={0.5} sx={{ minWidth: 0 }}>
              <Txt
                sx={{
                  fontFamily: MONO,
                  fontSize: 10,
                  color: C.faint,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {[
                  syncId,
                  closed
                    ? trade
                      ? "settled"
                      : "closed"
                    : soft
                      ? "est."
                      : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Txt>
              <FlagMarks trade={trade} flags={flags} closed={closed} />
            </Flx>
          </Box>
        </Flx>
      </Box>
    </Tooltip>
  );
};

export default ClosingChip;
